import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import useSessionStorage from "hooks/useSessionStorage"
import { fetchArticleData } from "utils/getArticleData"

const usePersonalizedArticles = ({ articleId, initialArticle, allArticles }) => {
  const articleIds = useRef([])
  const [loadedArticles, setLoadedArticles] = useState(() =>
    initialArticle ? [initialArticle] : [],
  )
  const [isLoading, setIsLoading] = useState(true)

  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [hasMoreArticles, setHasMoreArticles] = useState(true)
  const [anonymousId, setAnonymousId] = useState(null)

  const personalizedRecommendationsLoaded = useRef(false)
  const [viewedArticles, setViewedArticles] = useSessionStorage("viewedArticles", [])
  const prefetchedArticle = useRef(null)
  const loadedArticleIds = useRef(new Set(initialArticle ? [initialArticle.id] : []))

  const cutoffDate = useMemo(() => {
    const oldestArticle = allArticles?.sort(
      (a, b) => new Date(a.publishDate) - new Date(b.publishDate),
    )[0]
    return (new Date(oldestArticle.publishDate).getTime() / 1000).toFixed(0)
  }, [allArticles])

  const shouldLoadMore = useCallback(
    () =>
      !isLoadingMore &&
      hasMoreArticles &&
      articleIds.current.length > 0 &&
      loadedArticles.length < 5 &&
      loadedArticles.length < articleIds.current.length,
    [isLoadingMore, hasMoreArticles, loadedArticles.length],
  )

  const isArticleLoaded = useCallback((id) => loadedArticleIds.current.has(id), [])

  const addArticle = useCallback(
    (article) => {
      if (!article || isArticleLoaded(article.id)) {
        return false
      }

      loadedArticleIds.current.add(article.id)
      setLoadedArticles((prev) => [...prev, article])
      setViewedArticles((prev) => [...prev, article.id])
      return true
    },
    [isArticleLoaded, setViewedArticles],
  )

  const getAnalyticsId = async () => {
    if (!anonymousId && typeof window !== "undefined" && window.analytics?.user) {
      try {
        const user = window.analytics.user()
        const id = user.anonymousId()

        if (id && !personalizedRecommendationsLoaded.current) {
          setAnonymousId(id)
          await getSuggestions(id)
          personalizedRecommendationsLoaded.current = true
          return true
        }
      } catch (err) {
        console.error("Error getting analytics ID:", err)
      }
    }
    setIsLoading(false)
    return false
  }

  useEffect(() => {
    if (articleId) {
      setViewedArticles((prev) => {
        if (!prev.includes(articleId)) {
          return [...prev, articleId]
        }
        return prev
      })
    }
  }, [articleId, setViewedArticles])

  useEffect(() => {
    const prefetchNextArticle = async () => {
      if (!shouldLoadMore() || prefetchedArticle.current) return

      const nextId = articleIds.current.find((id) => !isArticleLoaded(id))
      if (!nextId) return

      try {
        const article = await fetchArticleData(nextId)
        if (article && !isArticleLoaded(article.id)) {
          if (typeof article.body[0] === "string") {
            article.category = article.category || "Mindfulness"
            article.body = article.body.map((element) => JSON.parse(element))
            article.previewImage = JSON.parse(article.previewImage)
            article.seo = JSON.parse(article.seo)
            article.funnel = JSON.parse(article.funnel)
            article.socialPromotion = JSON.parse(article.socialPromotion)
            article.instructorClass = JSON.parse(article.instructorClass)
            article.recipes = article.recipes
              ? article.recipes.map((recipe) => JSON.parse(recipe))
              : null
            article.schema = JSON.parse(article.schema)
            article.recipeSchema = JSON.parse(article.recipeSchema)
            article.dataLayer = JSON.parse(article.dataLayer)
          }
          prefetchedArticle.current = article
        }
      } catch (err) {
        console.error("Error prefetching next article:", err)
      }
    }

    prefetchNextArticle()
  }, [articleIds.current, shouldLoadMore, isArticleLoaded])

  const getSuggestions = async (id) => {
    if (!id || personalizedRecommendationsLoaded.current) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(process.env.GATSBY_PERSONALIZATION_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          anonymousId: id,
          cutoffDate,
          itemCount: 50,
        }),
      })

      if (!response.ok) {
        throw new Error(response.statusText)
      }
      const results = await response.json()

      if (results.items) {
        let recommendedIds = results.items.filter((itemId) => !viewedArticles.includes(itemId))

        if (recommendedIds.length < 5) {
          const existingIds = new Set(recommendedIds)
          const availableArticles = allArticles
            .filter(
              (article) =>
                !existingIds.has(article.id) &&
                !viewedArticles.includes(article.id) &&
                article.template !== "clean",
            )
            .sort(() => Math.random() - 0.5)

          const additionalIds = availableArticles
            .slice(0, 5 - recommendedIds.length)
            .map((article) => article.id)

          recommendedIds = [...recommendedIds, ...additionalIds]
        }

        articleIds.current = recommendedIds
        personalizedRecommendationsLoaded.current = true
      }
    } catch (err) {
      console.error("Error fetching personalized articles:", err)
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const loadNextArticle = async (isSearchBot) => {
    if (isSearchBot) {
      setIsLoadingMore(false)
      setIsLoading(false)
      setError(null)
      setHasMoreArticles(false)
      setLoadedArticles([initialArticle])
      return initialArticle
    }

    setIsLoadingMore(true)

    if (!anonymousId) {
      await getAnalyticsId()
    }

    try {
      if (articleIds.current.length === 0 && !personalizedRecommendationsLoaded.current) {
        await getSuggestions(anonymousId)
      }

      if (prefetchedArticle.current) {
        const article = prefetchedArticle.current
        prefetchedArticle.current = null
        if (addArticle(article)) {
          return article
        }
      }

      const nextId = articleIds.current.find((id) => !isArticleLoaded(id))
      if (!nextId) {
        setHasMoreArticles(false)
        return null
      }

      const nextArticle = await fetchArticleData(nextId)
      if (!nextArticle) {
        throw new Error(`Failed to fetch article ${nextId}`)
      }

      if (typeof nextArticle.body[0] === "string") {
        nextArticle.category = nextArticle.category || "Mindfulness"
        nextArticle.body = nextArticle.body.map((element) => JSON.parse(element))
        nextArticle.previewImage = JSON.parse(nextArticle.previewImage)
        nextArticle.seo = JSON.parse(nextArticle.seo)
        nextArticle.funnel = JSON.parse(nextArticle.funnel)
        nextArticle.socialPromotion = JSON.parse(nextArticle.socialPromotion)
        nextArticle.instructorClass = JSON.parse(nextArticle.instructorClass)
        nextArticle.recipes = nextArticle.recipes
          ? nextArticle.recipes.map((recipe) => JSON.parse(recipe))
          : null
        nextArticle.schema = JSON.parse(nextArticle.schema)
        nextArticle.recipeSchema = JSON.parse(nextArticle.recipeSchema)
        nextArticle.dataLayer = JSON.parse(nextArticle.dataLayer)
      }

      if (addArticle(nextArticle)) {
        return nextArticle
      }
      return null
    } catch (err) {
      console.error("Error loading next article:", err)
      setError(err)
      setHasMoreArticles(false)
    } finally {
      setIsLoadingMore(false)
    }

    return null
  }

  return {
    articleIds: articleIds.current,
    anonymousId,
    loadedArticles,
    viewedArticles,
    setViewedArticles,
    isLoading,
    isLoadingMore,
    error,
    hasMoreArticles,
    hasLoaded: personalizedRecommendationsLoaded.current,
    loadNextArticle,
    getAnalyticsId,
  }
}

export default usePersonalizedArticles
